/**
 * useLiquidationGuard — Composable Validator Pattern
 * ───────────────────────────────────────────────────
 * Single source of truth for "what blocks liquidation?".
 * Each validator is a pure function; the hook composes them and
 * returns a structured result that the UI consumes declaratively.
 *
 * Adding a new rule = add a new validator. No changes required in
 * EmbarqueDetailModal or in CompliancePill.
 */
import { useMemo } from "react";
import { normaliseTipo } from "@/lib/documentTypes";
import {
  getApplicableRules,
  type ComplianceContext,
  type ComplianceRule,
} from "@/lib/complianceRules";
import { useComplianceRules } from "./useComplianceRules";

export type GuardSeverity = "critical" | "warning";

export interface GuardBlockerMeta {
  /** Document this blocker refers to (enables deep-link to AuditPanel). */
  documentId?: string;
  /** Human label for the document (e.g. "Factura COMERCIAL-2024-001"). */
  documentLabel?: string;
  /** Tipo normalizado (factura internacional, packing list, etc.) */
  documentType?: string;
  /** Optional count of items affected within the document. */
  count?: number;
  /** For cross-doc integrity blockers: the conflicting HS values. */
  conflict?: { hsA: string; hsB: string; sku?: string };
}

export interface GuardBlocker {
  id: string;
  severity: GuardSeverity;
  title: string;
  message: string;
  meta?: GuardBlockerMeta;
}

export interface ComplianceItemStatus {
  rule: ComplianceRule;
  present: boolean;
}

export interface LiquidationGuardResult {
  /** True when no critical blockers exist. UI uses this to disable the button. */
  canLiquidate: boolean;
  /** All findings (critical + warning) sorted by severity. */
  blockers: GuardBlocker[];
  /** Compliance checklist (one entry per applicable rule). */
  complianceChecklist: ComplianceItemStatus[];
  /** Aggregate counts for the pill UI. */
  summary: {
    totalRules: number;
    fulfilled: number;
    missingCritical: number;
    missingWarning: number;
  };
}

interface GuardInput {
  orgId?: string | null;
  isLiquidado?: boolean;
  trmAduana?: number | null;
  clienteId?: string | null;
  totalPesoKg?: number;
  modoTransporte?: "Marítimo" | "Aéreo" | "Terrestre" | null;
  fobTotalUSD?: number;
  /** True when the active DIM has both numero_dim & numero_levante populated. */
  dimDataComplete?: boolean;
  documents: Array<{
    id?: string;
    no_documento?: string | null;
    tipo_documento?: string | null;
  }>;
  /**
   * Inventory items in the shipment — used to detect AI-suggested HS Codes pending HITL validation
   * AND to verify HS Code integrity across documents (Factura ↔ BL ↔ Packing ↔ DIM).
   * Pass ALL inventory rows of the shipment, not just COGS — the integrity validator
   * needs to compare across document types.
   */
  items?: Array<{
    documento_id?: string;
    sku?: string | null;
    hs_code?: string | null;
    hs_code_deducido_por_ia?: boolean | null;
  }>;
}

// ── Document type helpers ─────────────────────────────────────────────
const COGS_TYPES = new Set(["factura internacional", "factura de compras"]);
const SUPPORT_TYPES_WITH_HS = new Set([
  "packing list",
  "bill of lading",
  "guia aerea",
  "guía aérea",
  "declaración de importación",
  "declaracion de importacion",
  "certificado de origen",
]);

function describeDoc(doc: { no_documento?: string | null; tipo_documento?: string | null }): string {
  const tipo = (doc.tipo_documento ?? "Documento").trim();
  const num = (doc.no_documento ?? "").trim();
  return num ? `${tipo} ${num}` : tipo;
}

/** Validates an HS Code shape: DIAN expects 10 digits (subpartida nacional). */
function isValidHsFormat(hs: string): boolean {
  const digits = hs.replace(/\D/g, "");
  return digits.length === 10;
}

// ── Atomic validators ─────────────────────────────────────────────────
function validateCliente(input: GuardInput): GuardBlocker | null {
  if (!input.clienteId) {
    return {
      id: "missing-cliente",
      severity: "critical",
      title: "Embarque huérfano",
      message: "Asigna un Cliente importador antes de liquidar.",
    };
  }
  return null;
}

function validateTRM(input: GuardInput): GuardBlocker | null {
  if (!input.trmAduana || input.trmAduana <= 0) {
    return {
      id: "missing-trm",
      severity: "critical",
      title: "TRM Aduana no definida",
      message: "Define la TRM Aduanera para que el motor pueda calcular el costo nacionalizado.",
    };
  }
  return null;
}

function validatePeso(input: GuardInput): GuardBlocker | null {
  if (!input.totalPesoKg || input.totalPesoKg === 0) {
    return {
      id: "zero-weight",
      severity: "warning",
      title: "Peso bruto en cero",
      message:
        "Sin peso, los gastos prorrateados por 'Peso' usarán 'Valor' como contingencia. El costo destino puede ser impreciso.",
    };
  }
  return null;
}

function validateModoTransporte(input: GuardInput): GuardBlocker | null {
  if (!input.modoTransporte) {
    return {
      id: "missing-modo-transporte",
      severity: "warning",
      title: "Modo de transporte no definido",
      message:
        "Define el modo (Marítimo, Aéreo o Terrestre) para activar las reglas condicionales de BL/Guía Aérea.",
    };
  }
  return null;
}

function validateMandatoryDocs(
  input: GuardInput,
  applicable: ComplianceRule[],
): { blockers: GuardBlocker[]; checklist: ComplianceItemStatus[] } {
  const presentTypes = new Set(
    input.documents.map((d) => normaliseTipo(d.tipo_documento)).filter(Boolean),
  );

  const checklist: ComplianceItemStatus[] = applicable.map((rule) => {
    // Special-case: the "dim-data" rule is satisfied only when the structured
    // fields (numero_dim + numero_levante) are present, not merely the PDF.
    if (rule.id === "dim-data") {
      return { rule, present: input.dimDataComplete === true };
    }
    return { rule, present: presentTypes.has(normaliseTipo(rule.tipoDocumento)) };
  });

  const blockers: GuardBlocker[] = checklist
    .filter((c) => !c.present)
    .map((c) => ({
      id: `missing-doc-${c.rule.id}`,
      severity: c.rule.severity,
      title: `Falta: ${c.rule.label}`,
      message: c.rule.descripcion,
    }));

  return { blockers, checklist };
}

/**
 * Capa 1 — DIAN Gatekeeping (granular por documento).
 * Mirror del backend RPC `commit_landed_cost`. Emite un blocker por documento
 * con `meta.documentId` para deep-link al AuditPanel.
 */
function validateHsCodeIA_perDocument(input: GuardInput): GuardBlocker[] {
  const docsById = new Map<string, { id: string; tipo: string; label: string }>();
  for (const d of input.documents) {
    if (!d.id) continue;
    const tipo = normaliseTipo(d.tipo_documento);
    if (!COGS_TYPES.has(tipo)) continue;
    docsById.set(d.id, { id: d.id, tipo, label: describeDoc(d) });
  }

  const countByDoc = new Map<string, number>();
  for (const it of input.items ?? []) {
    if (it?.hs_code_deducido_por_ia !== true) continue;
    if (!it.documento_id || !docsById.has(it.documento_id)) continue;
    countByDoc.set(it.documento_id, (countByDoc.get(it.documento_id) ?? 0) + 1);
  }

  const blockers: GuardBlocker[] = [];
  for (const [docId, count] of countByDoc) {
    const doc = docsById.get(docId)!;
    blockers.push({
      id: `hs-ia-pending::${docId}`,
      severity: "critical",
      title: `${doc.label} — ${count} HS sin validar`,
      message:
        "La IA sugirió subpartidas en este documento. Confírmalas o edítalas en Auditoría antes de congelar el costeo.",
      meta: { documentId: docId, documentLabel: doc.label, documentType: doc.tipo, count },
    });
  }
  return blockers;
}

/**
 * Capa 2 — Integridad cruzada de HS Code (Factura ↔ BL ↔ Packing ↔ DIM ↔ Cert. Origen).
 * Una discrepancia entre la subpartida declarada en la Factura y la del documento
 * de soporte es señal de error OCR o manipulación documental — bloqueante.
 */
function validateHsCodeIntegrity(input: GuardInput): GuardBlocker[] {
  const docsById = new Map<string, { id: string; tipo: string; label: string }>();
  for (const d of input.documents) {
    if (!d.id) continue;
    docsById.set(d.id, { id: d.id, tipo: normaliseTipo(d.tipo_documento), label: describeDoc(d) });
  }

  const cogsHsSet = new Set<string>();
  for (const it of input.items ?? []) {
    if (!it.documento_id) continue;
    const doc = docsById.get(it.documento_id);
    if (!doc || !COGS_TYPES.has(doc.tipo)) continue;
    if (it.hs_code_deducido_por_ia === true) continue;
    const hs = (it.hs_code ?? "").trim();
    if (hs) cogsHsSet.add(hs);
  }
  if (cogsHsSet.size === 0) return [];

  const conflictByDoc = new Map<string, { sku?: string; hsB: string }>();
  for (const it of input.items ?? []) {
    if (!it.documento_id) continue;
    const doc = docsById.get(it.documento_id);
    if (!doc || !SUPPORT_TYPES_WITH_HS.has(doc.tipo)) continue;
    const hs = (it.hs_code ?? "").trim();
    if (!hs || cogsHsSet.has(hs)) continue;
    if (!conflictByDoc.has(it.documento_id)) {
      conflictByDoc.set(it.documento_id, { sku: it.sku ?? undefined, hsB: hs });
    }
  }

  const refSample = Array.from(cogsHsSet).slice(0, 2).join(", ");
  return Array.from(conflictByDoc.entries()).map(([docId, conflict]) => {
    const doc = docsById.get(docId)!;
    return {
      id: `hs-mismatch::${docId}`,
      severity: "critical" as GuardSeverity,
      title: `${doc.label} — HS inconsistente con la Factura`,
      message: `Declara HS ${conflict.hsB}${conflict.sku ? ` (SKU ${conflict.sku})` : ""}, pero la Factura usa ${refSample}${cogsHsSet.size > 2 ? "…" : ""}. Resuelve la discrepancia antes de liquidar.`,
      meta: {
        documentId: docId,
        documentLabel: doc.label,
        documentType: doc.tipo,
        conflict: { hsA: refSample, hsB: conflict.hsB, sku: conflict.sku },
      },
    };
  });
}

/**
 * Capa 3 — Formato HS Code (DIAN: subpartida nacional = 10 dígitos).
 * Atrapa typos de OCR. Warning, no critical, porque algunos OCR históricos
 * almacenan 6 u 8 dígitos y la DIM completa el resto.
 */
function validateHsCodeFormat(input: GuardInput): GuardBlocker[] {
  const docsById = new Map<string, { id: string; tipo: string; label: string }>();
  for (const d of input.documents) {
    if (!d.id) continue;
    const tipo = normaliseTipo(d.tipo_documento);
    if (!COGS_TYPES.has(tipo)) continue;
    docsById.set(d.id, { id: d.id, tipo, label: describeDoc(d) });
  }

  const badByDoc = new Map<string, number>();
  for (const it of input.items ?? []) {
    if (!it.documento_id || !docsById.has(it.documento_id)) continue;
    if (it.hs_code_deducido_por_ia === true) continue;
    const hs = (it.hs_code ?? "").trim();
    if (!hs || isValidHsFormat(hs)) continue;
    badByDoc.set(it.documento_id, (badByDoc.get(it.documento_id) ?? 0) + 1);
  }

  return Array.from(badByDoc.entries()).map(([docId, count]) => {
    const doc = docsById.get(docId)!;
    return {
      id: `hs-format::${docId}`,
      severity: "warning" as GuardSeverity,
      title: `${doc.label} — ${count} HS con formato dudoso`,
      message:
        "Algunos códigos no cumplen el formato DIAN de 10 dígitos. Revisa posibles errores de OCR.",
      meta: { documentId: docId, documentLabel: doc.label, documentType: doc.tipo, count },
    };
  });
}

// ── Hook ──────────────────────────────────────────────────────────────
export function useLiquidationGuard(input: GuardInput): LiquidationGuardResult {
  const rules = useComplianceRules(input.orgId);

  return useMemo(() => {
    // Liquidated shipments report a fully-cleared, "frozen" state.
    if (input.isLiquidado) {
      return {
        canLiquidate: false,
        blockers: [],
        complianceChecklist: [],
        summary: { totalRules: 0, fulfilled: 0, missingCritical: 0, missingWarning: 0 },
      };
    }

    const ctx: ComplianceContext = {
      modoTransporte: input.modoTransporte ?? null,
      fobTotalUSD: input.fobTotalUSD ?? 0,
      dimDataComplete: input.dimDataComplete ?? false,
    };

    const applicable = getApplicableRules(rules, ctx);
    const { blockers: docBlockers, checklist } = validateMandatoryDocs(input, applicable);

    const guardBlockers: GuardBlocker[] = [
      validateCliente(input),
      validateTRM(input),
      validateModoTransporte(input),
      validatePeso(input),
      ...validateHsCodeIA_perDocument(input),
      ...validateHsCodeIntegrity(input),
      ...validateHsCodeFormat(input),
      ...docBlockers,
    ].filter((b): b is GuardBlocker => b !== null);

    // Sort: critical first, then warning.
    guardBlockers.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "critical" ? -1 : 1));

    const fulfilled = checklist.filter((c) => c.present).length;
    const missingCritical = guardBlockers.filter((b) => b.severity === "critical").length;
    const missingWarning = guardBlockers.filter((b) => b.severity === "warning").length;

    return {
      canLiquidate: missingCritical === 0,
      blockers: guardBlockers,
      complianceChecklist: checklist,
      summary: {
        totalRules: checklist.length,
        fulfilled,
        missingCritical,
        missingWarning,
      },
    };
  }, [
    input.isLiquidado,
    input.clienteId,
    input.trmAduana,
    input.totalPesoKg,
    input.modoTransporte,
    input.fobTotalUSD,
    input.dimDataComplete,
    input.documents,
    input.items,
    rules,
  ]);
}
